import {
  CGMType,
  DataSource,
} from "@tandemdiabetes/sugarmate-api-react-client"
import classNames from "classnames"
import {
  forwardRef,
  useMemo,
} from "react"
import type {
  ComponentPropsWithoutRef,
  Ref,
} from "react"

import { TooltipArrow } from "./TooltipArrow"
import styles from "./TooltipContent.module.scss"
import { TrendIcon } from "src/components"
import { usePolyglot } from "src/contexts"
import {
  useDisplayTime,
  useReadableGlucoseString,
} from "src/hooks"
import type { Reading } from "src/types"

interface Props extends ComponentPropsWithoutRef<"div"> {
  reading: Reading;
}

const prefix = "pages.home.graph.tooltip"
export const TooltipContent = forwardRef<HTMLDivElement, Props>(
  function TooltipContent(props: Props, propsRef: Ref<HTMLDivElement>) {
    const {
      reading,
      ...htmlDivProps
    } = props

    const polyglot = usePolyglot()
    const displayTime = useDisplayTime()
    const timeLabel = displayTime(reading.startedAt)
    const glucoseString = useReadableGlucoseString(
      reading.glucose.mg_dl,
    )

    const sourceLabelKey = useMemo(
      () => getSourceLabelKey(reading),
      [reading],
    )

    return (
      <div
        {...htmlDivProps}
        ref={propsRef}
        className={classNames(
          props.className,
          styles.container,
        )}
      >
        <div id="graph-time">
          {timeLabel}
        </div>
        <div
          className={styles.reading}
          id="graph-glucose-reading"
        >
          {glucoseString}
          {reading.glucose.trend && (
            <TrendIcon
              id="graph-trend-line"
              className={styles.trendIcon}
              trend={reading.glucose.trend}
            />
          )}
        </div>
        {sourceLabelKey && (
          <div
            className={styles.source}
            id="graph-data-source"
          >
            {polyglot.t(sourceLabelKey)}
          </div>
        )}
        <TooltipArrow />
      </div>
    )
  },
)

function getSourceLabelKey(reading: Reading): string | undefined {
  if (reading.readingType === "entry") {
    // all glucose entries are BG Readings
    return `${prefix}.source.bg_reading`
  }

  switch (reading.glucose.cgm_type) {
    /* eslint-disable @typescript-eslint/no-unsafe-enum-comparison */
    case CGMType.DexcomG4:
    case CGMType.DexcomG5:
    case CGMType.DexcomG6:
    case CGMType.DexcomG6Plus:
    case CGMType.DexcomPro:
    case CGMType.DexcomG7:
    case CGMType.AbbottFreeStyleLibre2:
    case CGMType.AbbottFreeStyleLibre3:
    /* eslint-enable @typescript-eslint/no-unsafe-enum-comparison */
      // if the cgm_type matches a CGM then the reading is from a CGM
      return `${prefix}.source.cgm_reading`
    default:
      switch (reading.source) {
        case DataSource.Dexcom:
        case DataSource.Nightscout:
        case DataSource.Tandem:
          // if the source is a CGM provider then the reading is from a CGM
          return `${prefix}.source.cgm_reading`
        case DataSource.AppleHealthKit:
        case DataSource.Sugarmate:
        case null:
        default:
          // we cannot determine if the reading came from a CGM or not
          return undefined
      }
  }
}
